import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { ALINEA_PROFILES } from '../utils/constants';
import { isWorkerRole, isSupervisorRole, effectiveAppRole } from '../utils/roles.js';
import { GlossaryText } from '../components/GlossaryTerm.jsx';
import QuizStepProgress from '../components/QuizStepProgress.jsx';
import FunctionalBaselineStep from '../components/FunctionalBaselineStep.jsx';
import { getCurrentHumanSeason, getCycleReviewIntro } from '../utils/humanSeasons.js';
import {
    loadHumanCycleConfig,
    resolveActiveSeason,
    getCycleReviewIntroFromConfig,
    getSeasonalQuestionFrameFromConfig,
    seasonToUiShape,
} from '../utils/humanCycles.js';
import {
    canListen,
    cancelSpeech,
    matchVoiceOption,
    quizOptionsForVoice,
    speakEs,
    startOneShotListen,
} from '../utils/voiceUx.js';
import './Survey.css';

/** Pantalla de éxito sin perfil maestro (operario, supervisor, admin). */
function usesSimpleEncuestaDone(rol) {
    const r = effectiveAppRole(rol);
    return r === 'worker' || r === 'supervisor' || r === 'admin' || r === 'superadmin';
}

export default function CuestionarioPerfil() {
    const { refreshUser, user } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const allowRetake = location.state?.retake === true;
    const completedRef = useRef(false);
    const redirectTimerRef = useRef(null);

    const [questions, setQuestions] = useState([]);
    const [currentIdx, setCurrentIdx] = useState(-1);
    const [answers, setAnswers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [result, setResult] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    /** intro | quiz | ef_t0 | done */
    const [phase, setPhase] = useState('intro');
    const [manadaStatus, setManadaStatus] = useState(null);
    const [cycleConfig, setCycleConfig] = useState(null);
    const [ttsOn, setTtsOn] = useState(true);
    const [listening, setListening] = useState(false);
    const [voiceHint, setVoiceHint] = useState('');
    const stopListenRef = useRef(null);
    const ttsOnRef = useRef(true);

    const season = cycleConfig
        ? seasonToUiShape(resolveActiveSeason(cycleConfig))
        : getCurrentHumanSeason();
    const cycleIntro = cycleConfig
        ? getCycleReviewIntroFromConfig(cycleConfig)
        : getCycleReviewIntro(season);

    const isCycleReview = Boolean(
        allowRetake || manadaStatus?.isCycleReview || (user?.hasProfile && user?.needsRegistroManada)
    );

    useEffect(() => {
        let cancelled = false;
        loadHumanCycleConfig().then((cfg) => {
            if (!cancelled) setCycleConfig(cfg);
        });
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        return () => {
            if (redirectTimerRef.current) clearTimeout(redirectTimerRef.current);
            cancelSpeech();
            stopListenRef.current?.();
        };
    }, []);

    useEffect(() => {
        ttsOnRef.current = ttsOn;
        if (!ttsOn) cancelSpeech();
    }, [ttsOn]);

    useEffect(() => {
        if (phase !== 'quiz' || currentIdx < 0) return undefined;
        const q = questions[currentIdx];
        if (!q?.question) return undefined;
        const frame = cycleConfig
            ? getSeasonalQuestionFrameFromConfig(cycleConfig, q.display_order ?? currentIdx + 1)
            : null;
        const toSpeak = [frame, q.question].filter(Boolean).join('. ');
        speakEs(toSpeak, ttsOnRef.current);
        setVoiceHint('');
        return () => cancelSpeech();
    }, [phase, currentIdx, questions, cycleConfig]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            await refreshUser();
            if (cancelled || allowRetake || completedRef.current || phase === 'done') return;
            try {
                const res = await api.getRegistroManadaEstado();
                const st = res?.data ?? res;
                if (!cancelled) setManadaStatus(st);
                if (st?.needsRegistroManada === false && st?.hasProfile === true) {
                    navigate('/', { replace: true });
                }
            } catch {
                /* fallback: efecto con user */
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [allowRetake, phase, navigate, refreshUser]);

    useEffect(() => {
        if (
            loading ||
            allowRetake ||
            !user ||
            completedRef.current ||
            phase === 'done' ||
            phase === 'ef_t0'
        ) {
            return;
        }
        if (user.needsRegistroManada === false && user.hasProfile === true) {
            navigate('/', { replace: true });
        }
    }, [loading, user, allowRetake, navigate, phase]);

    useEffect(() => {
        let cancelled = false;
        async function fetchQuestions() {
            setLoadError('');
            try {
                const res = await api.getProfileQuestions();
                const list = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
                if (!cancelled) {
                    setQuestions(list);
                    if (!list.length) {
                        setLoadError('No hay preguntas de encuesta configuradas para tu empresa.');
                    }
                }
            } catch (err) {
                console.error('Error loading profile questions', err);
                if (!cancelled) {
                    setLoadError('No se pudo cargar la encuesta. Comprueba que la API esté en marcha.');
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        }
        fetchQuestions();
        return () => {
            cancelled = true;
        };
    }, []);

    async function goToHome() {
        if (redirectTimerRef.current) clearTimeout(redirectTimerRef.current);
        let next = await refreshUser();
        if (next?.needsRegistroManada) {
            await new Promise((r) => setTimeout(r, 350));
            next = await refreshUser();
        }
        navigate('/', { replace: true, state: { encuestaCompletada: true } });
    }

    function scheduleAutoRedirect() {
        if (redirectTimerRef.current) clearTimeout(redirectTimerRef.current);
        redirectTimerRef.current = setTimeout(() => {
            goToHome();
        }, 1200);
    }

    const handleAnswer = (value) => {
        cancelSpeech();
        stopListenRef.current?.();
        setListening(false);
        setVoiceHint('');
        const newAnswers = [...answers];
        newAnswers[currentIdx] = value;
        setAnswers(newAnswers);

        if (currentIdx < questions.length - 1) {
            setCurrentIdx(currentIdx + 1);
        } else {
            submitQuiz(newAnswers);
        }
    };

    function startQuizVoice() {
        const q = questions[currentIdx];
        if (!q || submitting || !canListen()) return;
        setVoiceHint('Te escucho. Di la opción o toca un botón.');
        stopListenRef.current?.();
        setListening(true);
        stopListenRef.current = startOneShotListen({
            onResult: (said) => {
                const hit = matchVoiceOption(said, quizOptionsForVoice(q.options));
                if (hit) handleAnswer(hit.key);
                else setVoiceHint(`Escuché: “${said}”. Toca una opción si no coincidió.`);
            },
            onError: () => setVoiceHint('No pude oír. Toca una opción o prueba Hablar otra vez.'),
            onEnd: () => setListening(false),
        }).stop;
    }

    const submitQuiz = async (finalAnswers) => {
        const payload = questions.map((_, i) => Number(finalAnswers[i]));
        if (
            payload.length !== questions.length ||
            payload.some((a) => !Number.isFinite(a) || ![1, 2, 3, 4].includes(a))
        ) {
            alert('Faltan respuestas. Revisa las preguntas anteriores e intenta de nuevo.');
            return;
        }

        setSubmitting(true);
        try {
            const res = await api.submitProfileQuiz(payload);
            const profile = res?.profile ?? res?.data?.profile;
            if (!profile?.type) {
                throw new Error(res?.error || 'Respuesta incompleta del servidor');
            }
            completedRef.current = true;
            setResult(profile);
            await refreshUser();
            let efOn = false;
            try {
                const st = await api.getFunctionalStateStatus();
                efOn = Boolean(st?.data?.efCoreEnabled);
            } catch {
                efOn = false;
            }
            if (efOn) {
                setPhase('ef_t0');
            } else {
                setPhase('done');
                scheduleAutoRedirect();
            }
        } catch (err) {
            alert('Error al guardar la encuesta: ' + (err.message || 'intenta de nuevo'));
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="loading-screen profile-quiz-loading">
                <div className="spinner" />
                <p className="text-muted mt-md">Cargando encuesta…</p>
            </div>
        );
    }

    if (!questions.length) {
        return (
            <div className="container animate-in profile-quiz-page" style={{ maxWidth: '560px', padding: '3rem 1.5rem', textAlign: 'center' }}>
                <h1 className="mb-md">Encuesta de manada</h1>
                <p className="text-muted mb-lg">{loadError || 'No hay preguntas disponibles en este momento.'}</p>
                <button type="button" className="btn btn-primary" onClick={goToHome}>
                    {isSupervisorRole(user?.rol) ? 'Ir al panel' : 'Volver al inicio'}
                </button>
            </div>
        );
    }

    if (phase === 'ef_t0') {
        return (
            <FunctionalBaselineStep
                onDone={() => {
                    setPhase('done');
                    scheduleAutoRedirect();
                }}
                onSkip={() => {
                    setPhase('done');
                    scheduleAutoRedirect();
                }}
            />
        );
    }

    if (phase === 'done' && (result || completedRef.current)) {
        const worker = isWorkerRole(user?.rol);
        const supervisor = isSupervisorRole(user?.rol);
        const simpleDone = usesSimpleEncuestaDone(user?.rol);

        if (simpleDone) {
            return (
                <div className="container animate-in" style={{ textAlign: 'center', padding: '3rem 1.5rem', maxWidth: '560px', margin: '0 auto' }}>
                    <div className="profile-reveal">
                        <h2 className="mb-md" style={{ fontWeight: 600 }}>
                            Encuesta guardada
                        </h2>
                        <div className="glass-card mb-lg" style={{ padding: '1.5rem', background: 'rgba(255,255,255,0.9)', textAlign: 'left' }}>
                            <p style={{ fontSize: '1.05rem', lineHeight: 1.65, color: '#334155', margin: 0 }}>
                                <GlossaryText>
                                    Guardamos tus respuestas para afinar mensajes y lecturas en la app. No es una calificación frente a tu
                                    equipo ni del administrador: es la misma encuesta de manada que completan tus operarios.
                                </GlossaryText>
                            </p>
                        </div>
                        <p className="text-muted mb-md" style={{ lineHeight: 1.5 }}>
                            {user?.rol === 'admin' || user?.rol === 'superadmin'
                                ? 'Ya puedes usar el panel de administración.'
                                : supervisor
                                  ? 'En un momento te llevamos a tu panel (personas, alertas y manada).'
                                  : 'En un momento te llevamos a tu jornada.'}
                        </p>
                        <p className="text-xs text-muted mb-xl">Redirigiendo automáticamente…</p>
                        <div className="profile-quiz-actions">
                            <button type="button" className="btn btn-primary btn-lg" onClick={goToHome}>
                                {user?.rol === 'admin' || user?.rol === 'superadmin'
                                    ? 'Ir al panel ahora'
                                    : supervisor
                                      ? 'Ir a mi equipo ahora'
                                      : 'Ir a mi jornada ahora'}
                            </button>
                            {(user?.rol === 'admin' || user?.rol === 'superadmin') && (
                                <>
                                    <button
                                        type="button"
                                        className="btn btn-secondary btn-lg"
                                        onClick={() => navigate('/preview/jornada-operario')}
                                    >
                                        Ver jornada del operario (opcional)
                                    </button>
                                    <p className="text-xs text-muted" style={{ marginTop: '0.75rem', lineHeight: 1.45 }}>
                                        Solo para conocer la experiencia del colaborador. No es obligatorio y no aparece
                                        como pendiente en tu cuenta.
                                    </p>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            );
        }

        const profileInfo = ALINEA_PROFILES[result.type] || {};
        return (
            <div className="container animate-in" style={{ textAlign: 'center', padding: '3rem 1.5rem' }}>
                <div className="profile-reveal">
                    <div className="reveal-emoji" style={{ fontSize: '5rem', marginBottom: '1rem' }}>{profileInfo.emoji}</div>
                    <h2 className="mb-md">Tu Perfil Maestro es: {profileInfo.name}</h2>
                    <div className="glass-card mb-lg" style={{ padding: '1.5rem', background: 'rgba(255,255,255,0.8)' }}>
                        <p style={{ fontSize: '1.1rem', lineHeight: '1.6', color: '#334155' }}>
                            {profileInfo.description}
                        </p>
                    </div>
                    <p className="text-muted mb-xl">
                        Este perfil nos ayudará a personalizar tu experiencia en ALINEA y apoyarte en tu camino a la maestría técnica.
                    </p>
                    <button type="button" className="btn btn-primary btn-lg" onClick={goToHome}>
                        Ir al inicio
                    </button>
                </div>
            </div>
        );
    }

    if (currentIdx === -1) {
        const introStyle = isCycleReview
            ? { background: season.softBg, borderRadius: '12px', padding: '1.25rem', marginBottom: '1.5rem' }
            : undefined;

        return (
            <div className="container animate-in profile-quiz-page" style={{ maxWidth: '600px', padding: '4rem 1.5rem', textAlign: 'center' }}>
                <h1 className="mb-md">{isCycleReview ? cycleIntro.title : 'Encuesta de manada'}</h1>
                {isCycleReview ? (
                    <div style={introStyle}>
                        <p className="mb-sm text-lg" style={{ fontWeight: 600, margin: 0 }}>
                            {cycleIntro.lead}
                        </p>
                        <p className="mb-md text-muted" style={{ lineHeight: 1.6, margin: '0.75rem 0 0' }}>
                            {cycleIntro.body}
                        </p>
                        <p className="text-sm" style={{ margin: '1rem 0 0', color: season.accent }}>
                            {cycleIntro.seasonLabel} · {cycleIntro.seasonTone}
                        </p>
                    </div>
                ) : (
                    <>
                        {isSupervisorRole(user?.rol) && (
                            <p className="mb-sm text-sm" style={{ color: 'var(--color-primary-dark)' }}>
                                Primera vez como supervisor: complete esta encuesta (≈2 min) para entrar a su equipo. No sustituye el
                                check-in diario de los operarios.
                            </p>
                        )}
                        <p className="mb-md text-lg text-muted">
                            <GlossaryText>
                                Encuesta oficial ALINEA, la misma en todas las empresas. Unos 2 minutos, sin respuestas correctas: solo cómo
                                te sientes trabajando hoy.
                            </GlossaryText>
                        </p>
                        <p className="mb-xl text-sm text-muted">
                            Después puedes renovarla cada trimestre desde Apoyos en el menú.
                        </p>
                    </>
                )}
                <div
                    className="glass-card mb-xl"
                    style={{
                        padding: '1.5rem',
                        background: isCycleReview ? season.softBg : 'var(--color-primary-light)',
                        color: isCycleReview ? season.accent : 'var(--color-primary-dark)',
                    }}
                >
                    ⏱️ {isCycleReview ? cycleIntro.durationHint : 'Tiempo estimado: 2 minutos'}
                </div>
                <button
                    type="button"
                    className="btn btn-primary btn-lg w-100"
                    onClick={() => {
                        setPhase('quiz');
                        setCurrentIdx(0);
                    }}
                >
                    {isCycleReview ? 'Regálate un minuto' : 'Comenzar encuesta'}
                </button>
            </div>
        );
    }

    const q = questions[currentIdx];
    if (!q) {
        return (
            <div className="container profile-quiz-page" style={{ maxWidth: '560px', padding: '2rem', textAlign: 'center' }}>
                <p className="text-muted mb-lg">Preparando la siguiente pregunta…</p>
                <button type="button" className="btn btn-secondary" onClick={goToHome}>
                    Ir al inicio
                </button>
            </div>
        );
    }

    const isWorker = isWorkerRole(user?.rol);

    const seasonalFrame = cycleConfig
        ? getSeasonalQuestionFrameFromConfig(cycleConfig, q.display_order ?? currentIdx + 1)
        : null;

    return (
        <div className="container animate-in profile-quiz-page" style={{ maxWidth: '600px', padding: '2rem 1.5rem', minHeight: '40vh' }}>
            <QuizStepProgress
                step={currentIdx + 1}
                totalSteps={questions.length}
                label="Pregunta"
            />

            <div className="quiz-voice-bar">
                <button
                    type="button"
                    className={`quiz-tts-btn${ttsOn ? ' on' : ''}`}
                    onClick={() => setTtsOn((v) => !v)}
                >
                    {ttsOn ? 'Leyendo en voz alta' : 'Leer en voz alta: off'}
                </button>
                {canListen() ? (
                    <button
                        type="button"
                        className={`quiz-mic-btn${listening ? ' on' : ''}`}
                        disabled={submitting}
                        onClick={startQuizVoice}
                    >
                        {listening ? 'Escuchando…' : 'Hablar'}
                    </button>
                ) : (
                    <span className="quiz-voice-hint">Hablar pide Chrome o Safari</span>
                )}
            </div>
            {voiceHint ? <p className="quiz-voice-hint">{voiceHint}</p> : null}

            <div className="quiz-question-shell question-card" key={`q-${currentIdx}`}>
                {seasonalFrame && (
                    <p className="text-sm mb-md" style={{ color: season.accent, lineHeight: 1.5, fontWeight: 500 }}>
                        {seasonalFrame}
                    </p>
                )}
                <h2 className="mb-xl" style={{ fontSize: '1.4rem', lineHeight: '1.4' }}>{q.question}</h2>
                <div className="quiz-options" style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {q.options.map((opt) => (
                        <button 
                            key={opt.value} 
                            className="btn-quiz-option"
                            onClick={() => handleAnswer(opt.value)}
                            disabled={submitting}
                        >
                            {opt.label}
                        </button>
                    ))}
                </div>
            </div>

            {submitting && (
                <div className="loading-overlay">
                    <div className="spinner" />
                    <p className="mt-md">{worker ? 'Guardando…' : 'Calibrando tu perfil maestro...'}</p>
                </div>
            )}
        </div>
    );
}
